import * as React from 'react';
import { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import styled from 'styled-components';
import { ClientPack, PaymentStatus, InvoiceState } from '../../models';
import { getMySubscriptionAsync, generateInvoicePdfAsync, startSubscriptionCheckoutAsync } from '../../ajax/requests';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { formatCurrency, formatDateLocalized } from './utils';
import SubscriptionDetailModal from './SubscriptionDetailModal';
import { startCheckout } from "@easypaypt/checkout-sdk";
import { useGeneralSettings } from '../../store/settings/hooks';
import UpdateInvoiceInfoModal from './UpdateInvoiceInfoModal';
import AppButton from '../../components/AppButton';

const ModalContent = styled.div<{ children?: React.ReactNode }>`
    margin: 0 auto;
`;

const SubscriptionList = styled.div<{ children?: React.ReactNode }>`
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    gap: var(--spacer-S);
`;

const SubscriptionItem = styled.div.attrs<{ onClick?: () => void }>(() => ({}))<{ children?: React.ReactNode; onClick?: () => void }>`
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 180px) minmax(0, 1fr) minmax(0, auto);
    gap: var(--spacer-M);
    padding: var(--spacer-M);
    background: var(--bg-light);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: var(--spacer-S);
    align-items: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: var(--bg-hover-light);
        box-shadow: 0 var(--border-medium) var(--spacer-S) rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: var(--spacer-S);
    }
`;

const DatesSection = styled.div<{ children?: React.ReactNode }>`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);

    .date-label {
        font-size: 12px;
        font-weight: 600;
        color: var(--text-muted);
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .date-value {
        font-size: 14px;
        color: var(--text-body);
    }
`;

const SummarySection = styled.div<{ children?: React.ReactNode }>`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);

    .pack-name {
        font-size: 16px;
        font-weight: 700;
        color: var(--appBlue);
        margin-bottom: var(--spacer-XXS);
    }

    .extras-list {
        font-size: 12px;
        color: var(--text-muted);
        line-height: 1.5;
    }

    .no-extras {
        font-size: 13px;
        color: var(--text-placeholder);
        font-style: italic;
    }
`;

const RightSection = styled.div<{ children?: React.ReactNode }>`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--spacer-XS);
    white-space: nowrap;

    .price-with-vat {
        font-size: 20px;
        font-weight: 700;
        color: var(--success-color);
    }

    .price-without-vat {
        font-size: 13px;
        font-weight: 500;
        color: var(--text-muted);
    }

    @media (max-width: 768px) {
        align-items: flex-start;
    }
`;

const NoInvoiceLabel = styled.div`
    font-size: 12px;
    color: var(--text-placeholder);
    font-style: italic;
    text-align: right;

    @media (max-width: 768px) {
        text-align: left;
    }
`;

const EmptyState = styled.div<{ children?: React.ReactNode }>`
    text-align: center;
    padding: var(--spacer-L);
    color: var(--text-muted);
    font-size: 14px;
`;

const LoadingState = styled.div<{ children?: React.ReactNode }>`
    text-align: center;
    padding: var(--spacer-L);
    color: var(--text-muted);
    font-size: 14px;
`;

interface SubscriptionHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const SubscriptionHistoryModal: React.FC<SubscriptionHistoryModalProps> = ({ isOpen, onClose }) => {
    const t = useTranslations()
    const settings = useGeneralSettings()
    const [subscriptions, setSubscriptions] = useState<ClientPack[]>([])
    const [loading, setLoading] = useState(false)
    const [payingSubscription, setPayingSubscription] = useState<string|undefined>()
    const [selectedSubscription, setSelectedSubscription] = useState<ClientPack | null>(null)
    const [showUpdateInvoiceInfo, setShowUpdateInvoiceInfo] = useState(false)
    const [subscriptionToPay, setSubscriptionToPay] = useState<string | null>(null)
    const [mbPayment, setMbPayment] = useState<{entity: string, reference: string, expirationDate: string} | null>(null)

    useEffect(() => {
        if (isOpen) {
            loadSubscriptions();
        }
    }, [isOpen]);

    const loadSubscriptions = async () => {
        setLoading(true);
        try {
            const data = await getMySubscriptionAsync();
            setSubscriptions(data);
        } catch (error) {
            console.error('Failed to load subscription history:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewInvoice = async (invoiceId: string) => {
        try {
            const result = await generateInvoicePdfAsync(invoiceId, true);
            window.open(result.url, '_blank');
        } catch (error) {
            console.error('Failed to generate invoice PDF:', error);
        }
    };

    const handlePayButtonClick = (subscriptionId: string) => {
        setSubscriptionToPay(subscriptionId);
        setShowUpdateInvoiceInfo(true);
    };

    const handleStartPayment = async (subscriptionId: string) => {
        try {
            setPayingSubscription(subscriptionId)

            const manifest = await startSubscriptionCheckoutAsync(subscriptionId);

            const res = startCheckout(manifest, {
                id: 'easypay-checkout',
                testing: manifest.testMode,
                logoUrl: settings.companyImageUrl ?? 'https://app.appointmint.pt/appointmint-64.png',
                onSuccess: (...params) => {
                    console.log('Checkout success', ...params)
                    const result = params[0] as { payment?: { method?: string; entity?: string; reference?: string; expirationDate?: string } }
                    setPayingSubscription(undefined)
                    res.unmount()
                    if (result?.payment?.method === 'mb') {
                        setMbPayment({
                            entity: result.payment.entity ?? '',
                            reference: result.payment.reference ?? '',
                            expirationDate: result.payment.expirationDate ?? '',
                        })
                    } else {
                        onClose()
                    }
                },
                onClose: (...params) => {
                    console.log('Checkout closed', ...params)
                    setPayingSubscription(undefined)
                    res.unmount()
                },
                onError: (error) => {
                    console.error(error, 'Payment error')
                    setPayingSubscription(undefined)
                    res.unmount()
                }
            })
        } catch (error) {
            console.error('Failed to start checkout:', error);
        }
    };

    const handleInvoiceInfoSaved = () => {
        if (subscriptionToPay) {
            handleStartPayment(subscriptionToPay);
            setSubscriptionToPay(null);
        }
    };

    return (<>
        <Modal isOpen={isOpen} toggle={onClose} size="xl">
            
            <ModalHeader toggle={onClose}>
                {t(TK.history)}
            </ModalHeader>
            <ModalBody>
                <ModalContent>
                    {loading ? (
                        <LoadingState>{t(TK.loading)}...</LoadingState>
                    ) : subscriptions.length === 0 ? (
                        <EmptyState>{t(TK.noSubscriptionsFound)}</EmptyState>
                    ) : (
                        <div>
                            <div style={{ marginBottom: 'var(--spacer-M)', display: 'flex', justifyContent: 'flex-end' }}>
                                <AppButton
                                    color="info"
                                    size="sm"
                                    onClick={() => setShowUpdateInvoiceInfo(true)}
                                >
                                    {t(TK.updateMyInvoiceInformation)}
                                </AppButton>
                            </div>
                            <SubscriptionList>
                                {subscriptions.map((subscription) => {
                                    if (payingSubscription && payingSubscription !== subscription.id) {
                                        return
                                    }

                                    const issuedInvoices = (subscription.invoices ?? []).filter(i => i.state === InvoiceState.Issued);
                                    const totalPriceWithoutVAT = subscription.price + subscription.extras.reduce((acc, curr) => acc + curr.value * curr.quantity, 0)
                                    const totalPriceWithVAT = totalPriceWithoutVAT * (1 + subscription.vat)
                                    const totalPaid = subscription.payments
                                        ?.filter(p => p.status === PaymentStatus.Manual || p.status === PaymentStatus.Success)
                                        .reduce((acc, curr) => acc + curr.value, 0) ?? 0
                                    const isPaid = totalPaid >= totalPriceWithVAT

                                    return (
                                        <SubscriptionItem
                                            key={subscription.id}
                                            onClick={() => setSelectedSubscription(subscription)}
                                        >
                                            <DatesSection>
                                                <div>
                                                    <div className="date-label">{t(TK.validFrom)}</div>
                                                    <div className="date-value">
                                                        {formatDateLocalized(subscription.validityFrom, t(TK.notAvailable))}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="date-label">{t(TK.validTo)}</div>
                                                    <div className="date-value">
                                                        {formatDateLocalized(subscription.validityTo, t(TK.notAvailable))}
                                                    </div>
                                                </div>
                                            </DatesSection>

                                            <SummarySection>
                                                <div className="pack-name">{subscription.packName}</div>
                                                {subscription.extras && subscription.extras.length > 0 ? (
                                                    <div className="extras-list">
                                                        {subscription.extras.map((extra, index) => (
                                                            <div key={index}>
                                                                {extra.name} {extra.quantity > 1 && `(x${extra.quantity})`}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="no-extras">{t(TK.noExtras)}</div>
                                                )}
                                            </SummarySection>

                                            <RightSection>
                                                <div className="price-with-vat">{formatCurrency(totalPriceWithVAT)}</div>
                                                <div className="price-without-vat">{t(TK.withoutVAT)}: {formatCurrency(totalPriceWithoutVAT)}</div>
                                                {!isPaid ?
                                                    <AppButton
                                                        color="success"
                                                        disabled={!!payingSubscription}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handlePayButtonClick(subscription.id);
                                                        }}
                                                    >
                                                        {t(TK.pay)}
                                                    </AppButton>
                                                : issuedInvoices.length > 0 ?
                                                    <>
                                                        {issuedInvoices.map((invoice) => (
                                                            <AppButton
                                                                key={invoice.id}
                                                                color="primary"
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleViewInvoice(invoice.id);
                                                                }}
                                                            >
                                                                {t(TK.viewInvoice)}
                                                            </AppButton>
                                                        ))}
                                                    </>
                                                : <NoInvoiceLabel>{t(TK.invoiceNotCreatedYet)}</NoInvoiceLabel>
                                                }
                                            </RightSection>
                                        </SubscriptionItem>
                                    )})
                                }
                            </SubscriptionList>

                            <div id="easypay-checkout" style={{justifySelf: 'center', marginTop: 'var(--spacer-S)'}}></div>
                        </div>
                    )}
                </ModalContent>
            </ModalBody>

            <SubscriptionDetailModal
                isOpen={!!selectedSubscription}
                onClose={() => setSelectedSubscription(null)}
                subscription={selectedSubscription}
            />

            <UpdateInvoiceInfoModal
                isOpen={showUpdateInvoiceInfo}
                onClose={() => {
                    setShowUpdateInvoiceInfo(false);
                    setSubscriptionToPay(null);
                }}
                onSave={handleInvoiceInfoSaved}
            />
        </Modal>

        <Modal isOpen={!!mbPayment} toggle={() => setMbPayment(null)} size="sm">
            <ModalBody style={{padding: 'var(--spacer-M)'}}>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--spacer-M)'}}>

                    <img
                        src="/payment-methods/MULTIBANCO.svg"
                        alt="Multibanco"
                        style={{height: '48px', objectFit: 'contain'}}
                    />

                    <div style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--spacer-XS)',
                        background: 'var(--bg-light)',
                        border: 'var(--border-thin) solid var(--appLigthGrey)',
                        borderRadius: 'var(--spacer-XS)',
                        overflow: 'hidden',
                    }}>
                        {[
                            { label: t(TK.entity), value: mbPayment?.entity },
                            { label: t(TK.reference), value: mbPayment?.reference },
                            { label: t(TK.validUntil), value: mbPayment?.expirationDate },
                        ].map(({ label, value }, i, arr) => (
                            <div key={label} style={{
                                padding: 'var(--spacer-S) var(--spacer-M)',
                                borderBottom: i < arr.length - 1 ? 'var(--border-thin) solid var(--appLigthGrey)' : 'none',
                            }}>
                                <div style={{fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 'var(--spacer-XXS)'}}>{label}</div>
                                <div style={{fontSize: '22px', fontWeight: 700, letterSpacing: '3px', color: 'var(--text-body)', fontFamily: 'monospace'}}>{value}</div>
                            </div>
                        ))}
                    </div>

                    <AppButton color="secondary" block onClick={() => setMbPayment(null)}>{t(TK.close)}</AppButton>
                </div>
            </ModalBody>
        </Modal>
    </>);
};

export default SubscriptionHistoryModal;
